import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { SendChatMessageCommand } from '../../../application/chat-messages/models/SendChatMessageCommand';
import { alerts } from '../../../shared/ui/Alerts';
import { sendChatMessageHandler } from '../../../application/chat-messages/handlers/SendChatMessageHandler';
import type { SendChatMessageHandlerResult } from '../../../application/chat-messages/models/SendChatMessageHandlerResult';
import { resolveChatSessionCapabilities } from '../../../domain/resolvers/resolveChatSessionCapabilities';
import { resolveSessionAgent } from '../../../domain/resolvers/resolveSessionAgent';
import { selectSelectedSession } from '../../../domain/selectors/selected/chat/selectSelectedSession';
import { acquireApiAccessTokenAsync, acquireNexusApiAccessTokenAsync } from '../../../services/auth/AuthService';
import { useProviderServices } from '../../../app/providers/AppServicesContext';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { appendMessages, removeMessage, updateMessageContent, updateMessageInfo } from '../../../redux/features/chatMessages/chatMessagesSlice';
import {
	setBotResponseStatus,
	setIsBulkUploadProcessing,
	setBulkStatusMessageId,
	setIsResponseGenerating,
	updateDraftInput,
} from '../../../redux/features/chatUi/chatUiSlice';
import { AuthorRoles, ChatMessage, ChatMessageType } from '../../../domain/messages/models/ChatMessage';
import { ChatProviderType } from '../../../domain/chat/models/ChatProviderType';
import updateChatSessionHandler from '../../../application/chat-sessions/handlers/UpdateChatSessionHandler';
import { UpdateChatSessionCommand } from '../../../application/chat-sessions/models/UpdateChatSessionCommand';
import { upsertSession } from '../../../redux/features/chatSessions/chatSessionsSlice';
import { setIsTitleGenerating } from '../../../redux/features/chatUi/chatUiSlice';
import { trackException } from '../../../app/telemetry/appInsights';
import { NexusAgent } from '../../../domain/agents/models/Agent';
import { setIsImporting } from '../../../redux/features/chatDocuments/chatDocumentsSlice';
import { handleDuplicate, sanitizeFileName } from '../../utils/FileUtils';
import { AgentCapabilities } from '../../../domain/agents/models/AgentCapabilities';
import { useNexusDocuments } from '../../Documents/hooks/useNexusDocuments';
import { useProGPTDocuments } from '../../Documents/hooks/useProGPTDocuments';
import useKnowledgeSources from './useKnowledgeSources';
import { UserRole } from '../../../domain/users/models/User';

export interface IImage {
	id: string;
	file: File;
	previewUrl: string;
}

export interface UseChatInputVm {
	draftInput: string;
	isBusy: boolean;
	isResponseGenerating: boolean;
	isTextAreaDisabled: boolean;
	isToolkitOpen: boolean;
	isPromptEnhancing: boolean;
	isBulkPromptProcessing: boolean;
	isDocumentUploading: boolean;
	canUndoPromptEnhancement: boolean;
	canEnhancePrompt: boolean;
	canShowToolkitButton: boolean;
	resolvedSessionAgent: NexusAgent | null;
	capabilities: AgentCapabilities | null;
	pendingImages: IImage[];
	onDraftInputChange: (value: string) => void;
	onToggleToolkit: () => void;
	onBulkPromptUpload: (files: FileList) => void;
	onDocumentUpload: (files: FileList) => void;
	onSubmit: () => Promise<void>;
	onAbort: () => void;
	onEnhancePrompt: () => Promise<void>;
	onUndoPromptEnhancement: () => void;
	addImage: (file: File) => void;
	removeImage: (id: string) => void;
}

const bulkPromptFailedPrefix = '__BULK_PROMPT_FAILED__:';
const bulkPromptCompletedPrefix = '__BULK_PROMPT_COMPLETED__:';
const bulkPromptProcessingText = 'Processing bulk prompts';
const bulkPromptCompletedText = 'Bulk prompt processing completed.';
const bulkPromptFailedText = 'Bulk prompt processing failed. Please try again.';
const bulkPromptDownloadFailedText = 'Bulk prompt processing finished, but the result file could not be downloaded.';

const MAX_FILE_COUNT_PROGPT = 100;
const MAX_FILE_COUNT_NEXUS = 15;
const MAX_FILE_SIZE_MB_PROGPT = 30;
const MAX_FILE_SIZE_MB_NEXUS = 20;
const MAX_FILE_SIZE_BYTES_PROGPT = MAX_FILE_SIZE_MB_PROGPT * 1024 * 1024;
const MAX_FILE_SIZE_BYTES_NEXUS = MAX_FILE_SIZE_MB_NEXUS * 1024 * 1024;

const fileSearchAllowedExtensions = ['doc', 'docx', 'ppt', 'pptx', 'txt', 'pdf', 'png', 'jpeg', 'jpg'];
const codeInterpreterAllowedExtensions = [
	'csv', 'xlsx', 'docx', 'html', 'java', 'json', 'md', 'pdf', 'php', 'pptx', 'py', 'rb',
	'tex', 'txt', 'css', 'jpeg', 'jpg', 'gif', 'png', 'tar', 'ts', 'xml', 'zip', 'c',
	'cpp', 'cs', 'doc', 'js', 'sh',
];
export const useChatInput = (): UseChatInputVm => {
	const dispatch = useAppDispatch();
	const providerServices = useProviderServices();
	const { instance, inProgress } = useMsal();
	const [isToolkitOpen, setIsToolkitOpen] = useState(false);
	const [isPromptEnhancing, setIsPromptEnhancing] = useState(false);
	const [canUndoPromptEnhancement, setCanUndoPromptEnhancement] = useState(false);
	const [originalPrompt, setOriginalPrompt] = useState('');
	const sessionId = useAppSelector((state) => state.chatUi.selectedSessionId);
	const session = useAppSelector(selectSelectedSession);
	const agentsById = useAppSelector((state) => state.agents.byId);
	const documents = useAppSelector((state) => sessionId ? state.documents.bySessionId[sessionId] ?? [] : []);
	const isDocumentUploading = useAppSelector((state) => sessionId ? state.documents.isImporting[sessionId] ?? 0 : 0) > 0;
	const isResponseGenerating = useAppSelector((state) =>
		sessionId ? state.chatUi.bySessionId[sessionId]?.isResponseGenerating ?? false : false,
	);
	const isBulkPromptProcessing = useAppSelector((state) =>
		sessionId ? state.chatUi.bySessionId[sessionId]?.isBulkUploadProcessing ?? false : false,
	);
	const bulkUploadFileName = useAppSelector((state) =>
		sessionId ? state.chatUi.bySessionId[sessionId]?.bulkUploadFileName : undefined,
	);
	const botResponseStatus = useAppSelector((state) => {
		const status = sessionId ? state.chatUi.bySessionId[sessionId]?.botResponseStatus ?? '' : '';
		return status;
	});
	const bulkStatusMessageId = useAppSelector((state) =>
		sessionId ? state.chatUi.bySessionId[sessionId]?.bulkStatusMessageId : undefined,
	);

	const streamingMessageId = useAppSelector((state) =>
		sessionId ? state.chatUi.bySessionId[sessionId]?.streamingMessageId : undefined,
	);
	const streamingMessageIdRef = useRef(streamingMessageId);
	useEffect(() => { streamingMessageIdRef.current = streamingMessageId; }, [streamingMessageId]);


	const streamingMessageContent = useAppSelector((state) => {
		if (!sessionId || !streamingMessageId) return '';
		return state.chatMessages.bySessionId[sessionId]?.find((m) => m.id === streamingMessageId)?.content ?? '';
	});

	// for blocking UI state of input
	const isBusy = isBulkPromptProcessing || isResponseGenerating || isPromptEnhancing;

	// for blocking draft input
	const isTextAreaDisabled = isPromptEnhancing;
	const activeUserInfo = useAppSelector((state) => state.app.activeUserInfo);

	// maintain local value so typing stays responsive and we do not dispatch to Redux on every keystroke.
	const [localDraftInput, setLocalDraftInput] = useState('');
	const [pendingImages, setPendingImages] = useState<IImage[]>([]);
	const pendingImagesRef = useRef<IImage[]>([]);
	pendingImagesRef.current = pendingImages;

	const bulkPromptDownloadStartedRef = useRef<string | null>(null);
	// maintain redux draft when session changes or component unmounts
	const reduxDraft = useAppSelector((state) =>
		sessionId ? state.chatUi.bySessionId[sessionId]?.draftInput ?? '' : '',
	);

	// Async calls (onSubmit) can finish after the user has switched sessions.
	// maintain ref for curr session id to avoid dispatching actions for a session that is no longer active
	const currentlySelectedSessionIdRef = useRef(sessionId);
	currentlySelectedSessionIdRef.current = sessionId;

	// Async callbacks and effect cleanups need the latest draft value even after React has closed over an older render.
	// We keep that latest local draft in a ref so submit/prompt-enhancement flows and session-switch cleanup can read the up-to-date text.
	const localDraftInputRef = useRef(localDraftInput);
	localDraftInputRef.current = localDraftInput;

	// ref to block multiple submits while one is in-flight
	const isSubmittingRef = useRef(false);

	const abortControllerRef = useRef<AbortController | null>(null);

	// tracks the user's input for the current in-flight request so onAbort can persist it
	const inFlightUserMessageRef = useRef<string>('');

	const thinkLonger = useAppSelector((state) =>
		sessionId ? state.chatUi.bySessionId[sessionId]?.thinkLonger ?? false : false,
	);

	const resolvedSessionAgent = useMemo(
		() => (session ? resolveSessionAgent(session, agentsById) as NexusAgent : null),
		[agentsById, session],
	);
	const capabilities = useMemo(
		() => (session ? resolveChatSessionCapabilities(session, resolvedSessionAgent) : null),
		[resolvedSessionAgent, session],
	);

	const canShowKnowledgeSources = capabilities?.supportsKnowledgeSources === true && activeUserInfo?.role !== UserRole.RestrictedAccess;
	
	const knowledgeSourcesVm = useKnowledgeSources({
			sessionId,
			agentsById,
			canShowKnowledgeSources,
		});
	const selectedAgents = useAppSelector((state) =>
			sessionId ? state.chatUi.bySessionId[sessionId]?.selectedAgents : knowledgeSourcesVm.knowledgeSourceAgents.map((agent) => agent.agentId),
		);
	const isNexusAgent = resolvedSessionAgent != null && resolvedSessionAgent.providerType === ChatProviderType.Nexus;
	const nexusDocuments = useNexusDocuments(sessionId ?? '', resolvedSessionAgent);
	const proGPTDocuments = useProGPTDocuments(sessionId ?? '');
	const canUsePromptEnhancer = capabilities?.supportsPromptEnhancer === true;
	const canShowToolkitButton = capabilities?.supportsToolkit === true;
	const canEnhancePrompt = canUsePromptEnhancer && !isBusy && localDraftInput.trim().length > 0;

	const setCurrentDraftInput = useCallback(
		(value: string): void => {
			localDraftInputRef.current = value;
			setLocalDraftInput(value);
		},
		[],
	);

	// checks if the sessionId passed is the same as the one currently selected to avoid updating state for a session that is no longer active (ex. if the user has switched sessions while an async operation was in-flight)
	const isActiveSession = useCallback(
		(candidateSessionId: string | null): boolean => currentlySelectedSessionIdRef.current === candidateSessionId,
		[],
	);

	useEffect(() => {
		setIsToolkitOpen(false);
		setIsPromptEnhancing(false);
		setCanUndoPromptEnhancement(false);
		setOriginalPrompt('');
		setCurrentDraftInput(reduxDraft);
		bulkPromptDownloadStartedRef.current = null;
		// We only want to hydrate/reset when the selected session changes.
		// Same-session reduxDraft updates should not rerun this effect because they would reset local UI state.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sessionId, setCurrentDraftInput]);

	useEffect(() => {
		// capture sessionid when the effect runs initially
		const capturedSessionId = sessionId;

		// We persist to Redux here, on session change/unmount, instead of on every keystroke.
		return () => {
			if (capturedSessionId) {
				dispatch(updateDraftInput({ sessionId: capturedSessionId, value: localDraftInputRef.current }));
			}
			// reset the ref so it doesn't block submissions on the next session
			isSubmittingRef.current = false;
			localDraftInputRef.current = '';
		};
	}, [sessionId, dispatch]);

	useEffect(() => {
		if (!canShowToolkitButton) {
			setIsToolkitOpen(false);
		}
	}, [canShowToolkitButton]);

	useEffect(() => {
		if (!sessionId || !isBulkPromptProcessing || !bulkUploadFileName) {
			return;
		}

		const isFailed = botResponseStatus.startsWith(`${bulkPromptFailedPrefix}${bulkUploadFileName}`);
		const isCompleted = botResponseStatus === `${bulkPromptCompletedPrefix}${bulkUploadFileName}`;

		if (isFailed) {
			alerts.error(bulkPromptFailedText);
			if (bulkStatusMessageId) {
				dispatch(updateMessageContent({ sessionId, messageId: bulkStatusMessageId, content: bulkPromptFailedText }));
				dispatch(setBulkStatusMessageId({ sessionId, value: null }));
			}
			dispatch(setIsBulkUploadProcessing({ sessionId, value: false }));
			dispatch(setBotResponseStatus({ sessionId, value: '' }));
			return;
		}

		// Wait for backend completion event before downloading.
		if (!isCompleted) {
			return;
		}

		if (bulkPromptDownloadStartedRef.current === bulkUploadFileName) {
			return;
		}

		bulkPromptDownloadStartedRef.current = bulkUploadFileName;

		const downloadAsync = async (): Promise<void> => {
			let completionMessage = bulkPromptCompletedText;
			try {
				const accessToken = await acquireApiAccessTokenAsync({
					msalInstance: instance,
				});

				const file = await providerServices.proGPT.sessionWrite.downloadBulkPromptsAsync(
					sessionId,
					bulkUploadFileName,
					accessToken,
				);

				const url = URL.createObjectURL(file);
				const anchor = document.createElement('a');
				anchor.href = url;
				anchor.download = file.name;
				document.body.appendChild(anchor);
				anchor.click();
				document.body.removeChild(anchor);
				URL.revokeObjectURL(url);
			} catch {
				completionMessage = bulkPromptDownloadFailedText;
				alerts.error(bulkPromptDownloadFailedText);
			} finally {
				if (bulkStatusMessageId) {
					dispatch(updateMessageContent({ sessionId, messageId: bulkStatusMessageId, content: completionMessage }));
					dispatch(setBulkStatusMessageId({ sessionId, value: null }));
				}
				dispatch(setIsBulkUploadProcessing({ sessionId, value: false }));
				dispatch(setBotResponseStatus({ sessionId, value: '' }));
			}
		};

		void downloadAsync();
	}, [
		botResponseStatus,
		bulkUploadFileName,
		bulkStatusMessageId,
		dispatch,
		inProgress,
		instance,
		isBulkPromptProcessing,
		providerServices.proGPT.sessionWrite,
		sessionId,
	]);

	const onDraftInputChange = useCallback((value: string): void => {
		setCurrentDraftInput(value);
		if (value === '') {
			setCanUndoPromptEnhancement(false);
			setOriginalPrompt('');
		}
	}, [setCurrentDraftInput]);

	const onToggleToolkit = useCallback((): void => {
		if (!canShowToolkitButton) {
			return;
		}

		setIsToolkitOpen((prev) => !prev);
	}, [canShowToolkitButton]);

	const onEnhancePrompt = useCallback(async (): Promise<void> => {
		if (!canUsePromptEnhancer || isBusy) {
			return;
		}

		const capturedSessionId = sessionId;

		const currentPrompt = localDraftInputRef.current;
		if (currentPrompt.trim().length === 0) {
			return;
		}

		setIsPromptEnhancing(true);
		setOriginalPrompt(currentPrompt);

		try {
			const accessToken = await acquireNexusApiAccessTokenAsync({
				msalInstance: instance,
			});
			const response = await providerServices.nexus.promptEnhancer.enhancePromptAsync(
				{ prompt: currentPrompt },
				accessToken,
			);
			const enhancedPrompt = response.enhancedPrompt?.trim();

			if (!enhancedPrompt) {
				throw new Error('Prompt enhancer returned an empty response.');
			}

			if (isActiveSession(capturedSessionId)) {
				setCurrentDraftInput(response.enhancedPrompt);
				setCanUndoPromptEnhancement(true);
			} else if (capturedSessionId) {
				dispatch(updateDraftInput({ sessionId: capturedSessionId, value: response.enhancedPrompt }));
			}
		} catch (error) {
			if (isActiveSession(capturedSessionId)) {
				setCanUndoPromptEnhancement(false);
			}
			alerts.error('An error occurred while improving the prompt. Please try again or contact support.');
			trackException(error, {
				source: 'useChatInput.onEnhancePrompt',
				error: error instanceof Error ? error.message : String(error),
			});
		} finally {
			if (isActiveSession(capturedSessionId)) {
				setIsPromptEnhancing(false);
			}
		}
	}, [canUsePromptEnhancer, dispatch, instance, isActiveSession, isBusy, providerServices.nexus.promptEnhancer, sessionId, setCurrentDraftInput]);

	const onUndoPromptEnhancement = useCallback((): void => {
		setCurrentDraftInput(originalPrompt);
		setCanUndoPromptEnhancement(false);
		setOriginalPrompt('');
	}, [originalPrompt, setCurrentDraftInput]);

	const onAbort = useCallback((): void => {
		if (!isResponseGenerating || !sessionId) return;

		if (session?.providerType === ChatProviderType.Nexus) {
			const userMessage = inFlightUserMessageRef.current;
			const botContent = streamingMessageContent ? `${streamingMessageContent}\n\n*(stopped)*` : undefined;
			acquireNexusApiAccessTokenAsync({ msalInstance: instance })
				.then(async (token) => {
					if (userMessage) {
						await providerServices.nexus.messages.savePartialMessageAsync(sessionId, userMessage, token, 'user');
					}
					if (botContent) {
						await providerServices.nexus.messages.savePartialMessageAsync(sessionId, botContent, token, 'assistant');
					}
				})
				.catch(console.error);
		} else if (session?.providerType === ChatProviderType.ProGPT) {
			const botContent = streamingMessageContent ? `${streamingMessageContent}\n\n*(stopped)*` : undefined;
			acquireApiAccessTokenAsync({ msalInstance: instance })
				.then(async (token) => {
					if (botContent) {
						await providerServices.proGPT.messages.savePartialMessageAsync(sessionId, botContent, token, AuthorRoles.Bot);
					}
				})
				.catch(console.error);
		}

		abortControllerRef.current?.abort();
		dispatch(setIsResponseGenerating({ sessionId, value: false }));
	}, [dispatch, instance, isResponseGenerating, providerServices.nexus.messages, session?.providerType, sessionId, streamingMessageContent]);

	const onBulkPromptUpload = useCallback((files: FileList): void => {
		const file = files[0];
		if (!file) {
			return;
		}

		if (!file.name.toLowerCase().endsWith('.xlsx')) {
			alerts.warning('Invalid file type. Bulk prompts should be uploaded as an Excel file.');
			return;
		}

		if (!sessionId || !session || session.providerType !== ChatProviderType.ProGPT) {
			alerts.warning('Bulk prompt upload is only available for ProGPT chat sessions.');
			return;
		}

		const uploadAsync = async (): Promise<void> => {
			try {
				const accessToken = await acquireApiAccessTokenAsync({
					msalInstance: instance
				});

				const uploadedFileName = await providerServices.proGPT.sessionWrite.uploadBulkPromptsAsync(
					sessionId,
					file,
					accessToken,
				);

				bulkPromptDownloadStartedRef.current = null;
				const bulkStatusMessage: ChatMessage = {
					id: `bulk_status_${sessionId}_${Date.now()}`,
					chatId: sessionId,
					type: ChatMessageType.Message,
					timestamp: Date.now(),
					userName: 'Assistant',
					content: '',
					authorRole: AuthorRoles.Bot,
				};
				dispatch(appendMessages({ sessionId, messages: [bulkStatusMessage] }));
				dispatch(setBulkStatusMessageId({ sessionId, value: bulkStatusMessage.id }));
				dispatch(setBotResponseStatus({ sessionId, value: bulkPromptProcessingText }));
				dispatch(
					setIsBulkUploadProcessing({
						sessionId,
						value: true,
						fileName: uploadedFileName,
					}),
				);
			} catch {
				alerts.error('Failed to upload bulk prompts. Please try again.');
				dispatch(setIsBulkUploadProcessing({ sessionId, value: false }));
				dispatch(setBotResponseStatus({ sessionId, value: '' }));
			}
		};

		void uploadAsync();
	}, [dispatch, inProgress, instance, providerServices.proGPT.sessionWrite, session, sessionId]);

	const onDocumentUpload = useCallback(async (files: FileList): Promise<void> => {
		if (files.length === 0 || !sessionId || !session) {
			return;
		}

		const ALLOWED_EXTENSIONS = (isNexusAgent && resolvedSessionAgent.allowCodeInterpreterAtConversationLevel)
			? codeInterpreterAllowedExtensions
			: fileSearchAllowedExtensions;
		const MAX_FILE_COUNT = isNexusAgent ? MAX_FILE_COUNT_NEXUS : MAX_FILE_COUNT_PROGPT;
		const MAX_FILE_SIZE_MB = isNexusAgent ? MAX_FILE_SIZE_MB_NEXUS : MAX_FILE_SIZE_MB_PROGPT;
		const MAX_FILE_SIZE_BYTES = isNexusAgent ? MAX_FILE_SIZE_BYTES_NEXUS : MAX_FILE_SIZE_BYTES_PROGPT;

		const existingFileNames = new Set(documents.map((doc) => doc.name));

		if (files.length > MAX_FILE_COUNT || files.length + documents.length > MAX_FILE_COUNT) {
			alerts.error(
				`You can only upload up to ${MAX_FILE_COUNT} files. Your current file count is: ${files.length + documents.length}. Please try uploading with fewer files.`,
			);
			return;
		}

		const filesArray = Array.from(files);

		// Validate and sanitize files
		for (let i = filesArray.length - 1; i >= 0; i--) {
			const file = filesArray[i];

			// Check file size
			if (file.size > MAX_FILE_SIZE_BYTES) {
				alerts.error(`File size exceeds the ${MAX_FILE_SIZE_MB} MB limit. Please choose a smaller file.`);
				filesArray.splice(i, 1);
				continue;
			}

			// Check file extension
			const fileExtension = file.name.split('.').pop()?.toLowerCase();
			if (!fileExtension || !ALLOWED_EXTENSIONS.includes(fileExtension)) {
				alerts.error(`This file type is not allowed. Please upload a different file.`);
				filesArray.splice(i, 1);
				continue;
			}

			// Sanitize and handle duplicates
			let sanitizedName = sanitizeFileName(file.name);
			if (existingFileNames.has(sanitizedName)) {
				sanitizedName = handleDuplicate(existingFileNames, sanitizedName, filesArray);
			}
			filesArray[i] = new File([file], sanitizedName, { type: file.type });
		}

		if (filesArray.length === 0) {
			return;
		}

		try {
			if (isNexusAgent) {
				await nexusDocuments.onUploadDocuments(filesArray);
			} else {
				await proGPTDocuments.onUploadDocuments(filesArray);
			}
			alerts.success('Documents uploaded successfully.');
		} catch (error) {
			alerts.error('Failed to upload documents. Please try again.');
			console.error('Failed to upload documents:', error);
		} finally {
			dispatch(setIsImporting({ sessionId, count: 0 }));
		}
	}, [dispatch, documents, inProgress, instance, providerServices, resolvedSessionAgent, session, sessionId]);

	const addImage = useCallback((file: File) => {
		const MAX_IMAGE_SIZE = 2 * 1024 * 1024;
		const MAX_IMAGE_COUNT = 3;
		if (pendingImagesRef.current.length >= MAX_IMAGE_COUNT) {
			alerts.warning(`You can attach up to ${MAX_IMAGE_COUNT} images per message.`);
			return;
		}
		if (file.size > MAX_IMAGE_SIZE) {
			alerts.error('Image exceeds the 2 MB size limit. Please choose a smaller file.');
			return;
		}
		const reader = new FileReader();
		reader.onload = () => {
			setPendingImages(prev => {
				if (prev.length >= MAX_IMAGE_COUNT) return prev;
				return [...prev, { id: crypto.randomUUID(), file, previewUrl: reader.result as string }];
			});
		};
		reader.readAsDataURL(file);
	}, []);

	const removeImage = useCallback((id: string) => {
		setPendingImages(prev => prev.filter(i => i.id !== id));
	}, []);

	const onSubmit = useCallback(async (): Promise<void> => {
		if (!sessionId || !session || isBusy || isSubmittingRef.current) {
			return;
		}

		const trimmedInput = localDraftInputRef.current.trim();
		const capturedImages = pendingImagesRef.current;
		if (!trimmedInput && capturedImages.length === 0) {
			return;
		}
		setPendingImages([]);

		const capturedSessionId = sessionId;

		isSubmittingRef.current = true;
		inFlightUserMessageRef.current = trimmedInput;
		abortControllerRef.current = new AbortController();
		const { signal } = abortControllerRef.current;

		// adding a placeholder message on the frontend optimistically since the response doesn't return the user's message, only the bot message.
		const placeholderUserMessage: ChatMessage = {
			id: `user_${sessionId}_${Date.now()}`,
			chatId: sessionId,
			type: ChatMessageType.Message,
			timestamp: Date.now(),
			userName: activeUserInfo?.username ?? 'user',
			content: trimmedInput,
			authorRole: AuthorRoles.User,
			images: capturedImages.length > 0 ? capturedImages.map(i => i.previewUrl) : undefined,
		};
		dispatch(upsertSession({ ...session, modifiedDate: Date.now() }));
		dispatch(
			appendMessages({
				sessionId,
				messages: [placeholderUserMessage],
			}),
		);
		setCurrentDraftInput('');
		dispatch(updateDraftInput({ sessionId, value: '' }));
		setCanUndoPromptEnhancement(false);
		setOriginalPrompt('');

		dispatch(setIsTitleGenerating({ sessionId: sessionId, value: true }));
		dispatch(setIsResponseGenerating({ sessionId, value: true }));

		let handlerResult: SendChatMessageHandlerResult | undefined;
		try {
			const accessToken = await acquireApiAccessTokenAsync({
				msalInstance: instance
			});

			const command: SendChatMessageCommand = {
				chatId: sessionId,
				input: trimmedInput,
				images: capturedImages.length > 0 ? capturedImages.map(i => i.file) : undefined,
				thinkLonger: session.providerType === ChatProviderType.ProGPT ? thinkLonger : undefined,
				selectedAgents: session.providerType === ChatProviderType.ProGPT ? selectedAgents : undefined,
			};


			handlerResult = await sendChatMessageHandler({
				command,
				session,
				accessToken,
				providerServices,
				signal,
			});
		} catch (error) {
			const wasAborted = signal.aborted || (error instanceof Error && error.name === 'AbortError');
			if (!wasAborted) {
				// if the message failed to send, remove the placeholder message and restore the draft input so the user doesn't lose their message
				// only update local state if the current session is the captured session to avoid overwriting the draft input of another session if the user has switched sessions while the message was sending
				if (isActiveSession(capturedSessionId) && localDraftInputRef.current.trim().length === 0) {
					setCurrentDraftInput(trimmedInput);
					dispatch(updateDraftInput({ sessionId: capturedSessionId, value: trimmedInput }));
				} else {
					dispatch(updateDraftInput({ sessionId: capturedSessionId, value: trimmedInput }));
				}
				dispatch(removeMessage({ sessionId, messageId: placeholderUserMessage.id }));
				trackException(error, {
					source: 'useChatInput.onSubmit.sendChatMessageHandler',
					error: error instanceof Error ? error.message : String(error),
				});
				alerts.error('Failed to send message. Please try again.');
			}
		} finally {
			if (isActiveSession(capturedSessionId)) {
				isSubmittingRef.current = false;
			}
			abortControllerRef.current = null;
			inFlightUserMessageRef.current = '';
			if (session.providerType !== ChatProviderType.Assistants) {
				dispatch(setIsResponseGenerating({
					sessionId,
					value: false,
					fallbackContent: handlerResult?.responseMessageText,
				}));
			}
			if (session.providerType !== ChatProviderType.Assistants && handlerResult?.agentSources) {
				const latestId = streamingMessageIdRef.current;
				dispatch(updateMessageInfo({ sessionId: sessionId, messageId: latestId ?? '', agentSources: handlerResult?.agentSources, tokenInfo: handlerResult?.tokens }))
			}
			if (session.providerType == ChatProviderType.ProGPT && handlerResult?.generatedTitle) {
				let result;

				try {
					const accessToken = await acquireApiAccessTokenAsync({
						msalInstance: instance
					});

					const command: UpdateChatSessionCommand = {
						sessionId: sessionId,
						title: handlerResult.generatedTitle,
						isTitleGenerated: true,
					};

					result = await updateChatSessionHandler({
						command,
						session,
						accessToken,
						providerServices,
						resolvedAgent: resolvedSessionAgent,
					})
					dispatch(upsertSession(result.session));
				}
				catch (error) {
					trackException(error, {
						source: 'useChatInput.onSubmit.updateChatSessionHandler',
						error: error instanceof Error ? error.message : String(error),
					});
					alerts.error('Failed to generate chat title.');
				}
			}
			dispatch(setIsTitleGenerating({ sessionId: sessionId, value: false }));
		}
	}, [dispatch, activeUserInfo?.id, activeUserInfo?.username, instance, isActiveSession, isPromptEnhancing, isBusy, providerServices, selectedAgents, session, sessionId, thinkLonger, resolvedSessionAgent, setCurrentDraftInput, streamingMessageId]);

	return useMemo(
		() => ({
			draftInput: localDraftInput,
			isBusy,
			isResponseGenerating,
			isTextAreaDisabled,
			isToolkitOpen,
			isBulkPromptProcessing,
			isDocumentUploading,
			isPromptEnhancing,
			canUndoPromptEnhancement,
			canEnhancePrompt,
			canShowToolkitButton,
			resolvedSessionAgent,
			capabilities,
			pendingImages,
			onDraftInputChange,
			onToggleToolkit,
			onBulkPromptUpload,
			onDocumentUpload,
			onSubmit,
			onAbort,
			onEnhancePrompt,
			onUndoPromptEnhancement,
			addImage,
			removeImage,
		}),
		[
			localDraftInput,
			isBusy,
			isResponseGenerating,
			isTextAreaDisabled,
			isToolkitOpen,
			isBulkPromptProcessing,
			isDocumentUploading,
			isPromptEnhancing,
			canUndoPromptEnhancement,
			canEnhancePrompt,
			canShowToolkitButton,
			resolvedSessionAgent,
			capabilities,
			pendingImages,
			onDraftInputChange,
			onToggleToolkit,
			onBulkPromptUpload,
			onDocumentUpload,
			onSubmit,
			onAbort,
			onEnhancePrompt,
			onUndoPromptEnhancement,
			addImage,
			removeImage,
		],
	);
};

export default useChatInput;
